import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import {
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import {
  dateFormatter,
  normaliseToDateInput,
} from "../../utils/numberFormatter";
import {
  parseDateOnly,
  parseInstantUtc,
  toDateOnlyString,
} from "../../utils/formatDate";
import type {
  Accordion,
  AccordionStatusHistoryItem,
  DealStatusHistoryItem,
  Status,
} from "../../types/deal";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
} from "../../services/genesisApi/httpsMethods";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { useCurrentUser } from "../../context/CurrentUserContext";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { CustomButton } from "../../components/common/customButton";
import { createSingleSelectColumn } from "../../utils/columnHelpers";
import "../../assets/css/AGGrid-custom.css";
import "../../assets/css/Text-General.css";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

type HistoryRow = DealStatusHistoryItem | AccordionStatusHistoryItem;

type RowData = Omit<DealStatusHistoryItem, "id"> & {
  _gridKey: string;
  /** Numeric for saved rows; string for rows added in the grid before save. */
  id: number | string | null;
};

const ADDON_GRID_HEIGHT = 220;

function getChangedAt(
  row: { changed_at?: string | null; date_changed?: string | null } | null | undefined,
): string | null {
  if (!row) return null;
  return row.changed_at ?? row.date_changed ?? null;
}

function getDurationDays(
  row: { duration_days?: number | null; days_in_status?: number | null } | null | undefined,
): number | null {
  if (!row) return null;
  const v = row.duration_days ?? row.days_in_status;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Calendar date (YYYY-MM-DD) → midnight UTC on that date for the API.
 * changed_at is a UTC DateTime and the grid shows its date part, so this
 * keeps the picked day in every timezone (local midnight via toISOString
 * is the previous day east of UTC).
 */
function calendarDateToIso(ymd: string): string | null {
  const t = ymd.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const day = toDateOnlyString(parseDateOnly(t));
  return day ? `${day}T00:00:00.000Z` : null;
}

function toIso(value: unknown): string | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  const t = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    return calendarDateToIso(t);
  }
  // API timestamps omit the offset but are UTC.
  const d = parseInstantUtc(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function withGridKeys(rows: HistoryRow[]): RowData[] {
  return rows.map((r, i): RowData => ({
    ...r,
    id:
      r.id ?? `syn-${i}-${r.status_name}-${getChangedAt(r) ?? ""}`,
    _gridKey:
      typeof r.id === "number"
        ? `id-${r.id}`
        : `syn-${i}-${r.status_name}-${getChangedAt(r) ?? ""}`,
  }));
}

function statusesSorted(statuses: Status[]): Status[] {
  return [...statuses].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
}

function positiveAccordionId(accordion: Accordion): number | null {
  return typeof accordion.id === "number" &&
    Number.isFinite(accordion.id) &&
    accordion.id > 0
    ? accordion.id
    : null;
}

/** The initial-deal accordion is the parent deal, not a separate timeline. */
function isInitialDealAccordion(accordion: Accordion): boolean {
  const raw = accordion.funding_type as unknown;
  const fundingType =
    typeof raw === "string"
      ? raw
      : raw &&
          typeof raw === "object" &&
          "id" in raw &&
          typeof (raw as { id: unknown }).id === "string"
        ? (raw as { id: string }).id
        : "";
  return fundingType.trim().toLowerCase().replace(/\s+/g, "-") === "initial-deal";
}

interface StatusHistoryGridProps {
  /** `deal/{id}/status-history` or `accordions/{id}/status-history`. */
  resourcePath: string;
  height?: number;
  title?: string;
  stacked?: boolean;
  /** Refetch when the Status History tab is shown. */
  active?: boolean;
}

const StatusHistoryGrid: FC<StatusHistoryGridProps> = ({
  resourcePath,
  height = 250,
  title,
  stacked = false,
  active = true,
}) => {
  const { canEdit } = useCurrentUser();
  const { status: statusList } = useReferenceLists();
  const sortedStatuses = useMemo(
    () => statusesSorted(statusList ?? []),
    [statusList],
  );

  const [rows, setRows] = useState<RowData[]>([]);
  const [initialRows, setInitialRows] = useState<RowData[]>([]);
  const [loading, setLoading] = useState(true);
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirtyGrid, setDirtyGrid] = useState(false);

  const statusNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sortedStatuses) map.set(s.id, s.status);
    return map;
  }, [sortedStatuses]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    const res = await apiGet<HistoryRow[]>(resourcePath);
    if (res.success && Array.isArray(res.data)) {
      const sorted = [...res.data].sort((a, b) => {
        const ta = getChangedAt(a);
        const tb = getChangedAt(b);
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return new Date(ta).getTime() - new Date(tb).getTime();
      });
      const gridRows = withGridKeys(sorted);
      setRows(gridRows);
      setInitialRows(structuredClone(gridRows));
      setDirtyGrid(false);
    } else {
      showErrorFromResponse(res);
      setRows([]);
      setInitialRows([]);
    }
    setLoading(false);
  }, [resourcePath]);

  useEffect(() => {
    if (!active) return;
    void loadHistory();
    // showErrorFromResponse comes from a hook and is not referentially stable.
    // Keeping the fetch keyed to resourcePath avoids accidental refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loadHistory]);

  const canEditRow = useCallback(
    (row: { id: number | string | null }) => typeof row.id === "number",
    [],
  );

  useEffect(() => {
    const normal = (input: RowData[]) =>
      input.map((r) => ({
        id: r.id,
        status_id: r.status_id ?? null,
        status_name: r.status_name ?? "",
        changed_at: getChangedAt(r),
        notes: r.notes ?? null,
        changed_by_user: r.changed_by_user ?? null,
      }));
    setDirtyGrid(JSON.stringify(normal(rows)) !== JSON.stringify(normal(initialRows)));
  }, [rows, initialRows]);

  const handleAddRow = useCallback(() => {
    setEditMode(true);
    const defaultStatus = sortedStatuses[0];
    const todayIso = calendarDateToIso(toDateOnlyString(new Date()));
    const next: RowData = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      _gridKey: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      status_id: defaultStatus?.id ?? null,
      status_name: defaultStatus?.status ?? "",
      changed_at: todayIso,
      notes: "",
      changed_by_user: "",
      is_current: false,
      duration_days: null,
    };
    setRows((prev) => [...prev, next]);
  }, [sortedStatuses]);

  const onCellValueChanged = useCallback((event: { data?: RowData; colDef?: { field?: string }; newValue?: unknown }) => {
    const row = event.data;
    const field = event.colDef?.field;
    if (!row || !field) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r._gridKey !== row._gridKey) return r;
        if (field === "status") {
          const nv = event.newValue as Status | null;
          if (nv && typeof nv === "object" && typeof nv.id === "number") {
            return {
              ...r,
              status_id: nv.id,
              status_name:
                nv.status ?? statusNameById.get(nv.id) ?? r.status_name ?? "",
            };
          }
          return {
            ...r,
            status_id: null,
            status_name: "",
          };
        }
        const next = { ...r, [field]: event.newValue } as RowData;
        if (field === "changed_at") {
          const iso = toIso(event.newValue);
          next.changed_at = iso ?? next.changed_at ?? null;
        }
        return next;
      }),
    );
  }, [statusNameById]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const initialByKey = new Map(initialRows.map((r) => [r._gridKey, r]));
      const changedRows = rows.filter((r) => {
        const initial = initialByKey.get(r._gridKey);
        if (!initial) return true;
        return JSON.stringify({
          status_id: r.status_id ?? null,
          changed_at: getChangedAt(r),
          notes: r.notes ?? null,
          changed_by_user: r.changed_by_user ?? null,
        }) !== JSON.stringify({
          status_id: initial.status_id ?? null,
          changed_at: getChangedAt(initial),
          notes: initial.notes ?? null,
          changed_by_user: initial.changed_by_user ?? null,
        });
      });

      // Rows without a status or date cannot be saved: report them instead of
      // silently skipping them (and dropping them on reload).
      const incompleteCount = changedRows.filter(
        (row) =>
          typeof row.status_id !== "number" || !toIso(getChangedAt(row)),
      ).length;
      if (incompleteCount > 0) {
        showError(
          undefined,
          `${incompleteCount} status history row${incompleteCount === 1 ? " is" : "s are"} missing a status or date. Fill them in or delete the row${incompleteCount === 1 ? "" : "s"} before saving.`,
          "Validation Error",
        );
        return;
      }

      // Rows written before a failure, so a retry does not save them twice
      const savedRows = new Map<string, RowData>();
      let failed = false;

      for (const row of changedRows) {
        const statusId = typeof row.status_id === "number" ? row.status_id : null;
        const changedAtIso = toIso(getChangedAt(row));
        if (!statusId || !changedAtIso) continue;

        if (typeof row.id === "number") {
          const body: Record<string, unknown> = {
            status_id: statusId,
            changed_at: changedAtIso,
            notes: row.notes?.toString().trim() || null,
          };
          const changedBy = row.changed_by_user?.toString().trim();
          if (changedBy) body.changed_by_user = changedBy;
          const res = await apiPatch(
            `${resourcePath}/${row.id}`,
            body,
          );
          if (!res.success) {
            showErrorFromResponse(res);
            failed = true;
            break;
          }
          savedRows.set(row._gridKey, row);
        } else {
          const body: Record<string, unknown> = {
            status_id: statusId,
            changed_at: changedAtIso,
          };
          const notes = row.notes?.toString().trim();
          if (notes) body.notes = notes;
          const changedBy = row.changed_by_user?.toString().trim();
          if (changedBy) body.changed_by_user = changedBy;
          const res = await apiPost(resourcePath, body);
          if (!res.success) {
            showErrorFromResponse(res);
            failed = true;
            break;
          }
          const createdId = (res.data as { record?: { id?: unknown } } | undefined)
            ?.record?.id;
          savedRows.set(row._gridKey, {
            ...row,
            id: typeof createdId === "number" ? createdId : row.id,
          });
        }
      }

      if (failed) {
        // Keep edit mode and the unsaved edits; treat rows already written as
        // the new baseline so retrying only sends what is still unsaved.
        if (savedRows.size > 0) {
          setRows((prev) => prev.map((r) => savedRows.get(r._gridKey) ?? r));
          setInitialRows((prev) => {
            const updated = prev.map((r) => {
              const saved = savedRows.get(r._gridKey);
              return saved ? structuredClone(saved) : r;
            });
            const existingKeys = new Set(prev.map((r) => r._gridKey));
            savedRows.forEach((saved, key) => {
              if (!existingKeys.has(key)) updated.push(structuredClone(saved));
            });
            return updated;
          });
        }
        return;
      }

      await loadHistory();
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  }, [
    rows,
    initialRows,
    resourcePath,
    loadHistory,
    showError,
    showErrorFromResponse,
  ]);

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSave);

  const handleCancel = useCallback(() => {
    setRows(structuredClone(initialRows));
    setDirtyGrid(false);
    setEditMode(false);
  }, [initialRows]);

  const handleDeleteRow = useCallback(async (row: RowData) => {
    if (!window.confirm("Are you sure you want to delete this status history row?")) {
      return;
    }
    if (typeof row.id === "number") {
      const res = await apiDelete(resourcePath, row.id);
      if (!res.success && !(res.status === 200 || res.status === 204)) {
        showErrorFromResponse(res);
        return;
      }
    }
    setRows((prev) => prev.filter((r) => r._gridKey !== row._gridKey));
    setInitialRows((prev) => prev.filter((r) => r._gridKey !== row._gridKey));
  }, [resourcePath, showErrorFromResponse]);

  const DeleteRenderer = useCallback(
    (params: ICellRendererParams<RowData, unknown>) => {
      const row = params.data;
      if (!row) return null;
      return (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            aria-label="Delete"
            color="error"
            disabled={!editMode}
            onClick={(ev) => {
              ev.stopPropagation();
              void handleDeleteRow(row);
            }}
            sx={{ opacity: editMode ? 1 : 0.35 }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Box>
      );
    },
    [editMode, handleDeleteRow],
  );

  const columnDefs: ColDef<RowData>[] = useMemo(() => {
    const base: ColDef<RowData>[] = [
      {
        ...(createSingleSelectColumn(
          "status",
          "Status",
          sortedStatuses,
          "status",
          140,
          editMode,
        ) as ColDef<RowData>),
        valueGetter: (params) => {
          const statusId = params.data?.status_id;
          if (statusId == null) return null;
          const found = sortedStatuses.find((s) => s.id === statusId);
          if (found) return found;
          return {
            id: statusId,
            status: params.data?.status_name ?? "",
            sort_order: 0,
          };
        },
        valueSetter: (params) => {
          if (!params.data) return false;
          const nv = params.newValue as Status | { id: null } | null;
          if (
            nv === null ||
            (typeof nv === "object" && nv !== null && nv.id === null)
          ) {
            params.data.status_id = null;
            params.data.status_name = "";
            return true;
          }
          if (
            nv &&
            typeof nv === "object" &&
            "id" in nv &&
            typeof nv.id === "number"
          ) {
            const st = nv as Status;
            params.data.status_id = st.id;
            params.data.status_name = st.status ?? "";
            return true;
          }
          return false;
        },
      },
      {
        headerName: "Date changed",
        width: 140,
        field: "changed_at",
        editable: editMode,
        cellEditor: "agDateStringCellEditor",
        valueGetter: (p) =>
          normaliseToDateInput(
            getChangedAt(p.data as DealStatusHistoryItem),
          ),
        valueFormatter: (
          params: ValueFormatterParams<RowData, string | null | undefined>,
        ) =>
          params.value ? dateFormatter({ value: params.value }) : "—",
      },
      {
        headerName: "Duration (days)",
        width: 130,
        valueGetter: (p) => getDurationDays(p.data as DealStatusHistoryItem),
        valueFormatter: (p) =>
          p.value != null && p.value !== "" ? String(p.value) : "—",
      },
      {
        field: "is_current",
        headerName: "Current",
        width: 90,
        valueFormatter: (p) => (p.value === true ? "Yes" : ""),
      },
      {
        field: "changed_by_user",
        headerName: "Changed by",
        width: 140,
        editable: editMode,
        valueFormatter: (p) => (p.value ? String(p.value) : "—"),
      },
      {
        field: "notes",
        headerName: "Notes",
        minWidth: 160,
        flex: 1,
        editable: editMode,
        valueFormatter: (p: { value?: unknown }) =>
          p.value ? String(p.value) : "—",
      },
    ];
    if (!editMode) {
      return base;
    }
    return [
      ...base,
      {
        headerName: "",
        width: 56,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        cellRenderer: DeleteRenderer,
      },
    ];
  }, [editMode, DeleteRenderer, sortedStatuses]);

  return (
    <Box sx={{ mt: stacked ? 3 : 0 }}>
      {title ? (
        <Typography
          component="h3"
          className="text-general-section-title"
          sx={{
            "&&": {
              fontWeight: "600 !important",
              marginBottom: "8px !important",
            },
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{
          height,
          width: "100%",
          position: "relative",
          "& .ag-row.status-history-row--editable": {
            cursor: "pointer",
          },
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              bgcolor: "rgba(255,255,255,0.6)",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact<RowData>
            {...gridEditProps}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={{ flex: 0, resizable: true, sortable: true, filter: true }}
            rowHeight={30}
            headerHeight={30}
            singleClickEdit={editMode}
            onCellValueChanged={onCellValueChanged}
            overlayNoRowsTemplate="No status history yet."
            getRowId={(p) => p.data?._gridKey ?? ""}
            suppressCellFocus
            getRowClass={(p) =>
              editMode &&
              p.data &&
              canEditRow(p.data)
                ? "status-history-row--editable"
                : ""
            }
          />
        </div>
      </Box>

      <Box display="flex" gap={2} mb={1} mt={2}>
        {editMode ? (
          <>
            <CustomButton
              text="Cancel"
              handleButtonPress={handleCancel}
              isButtonDisabled={saving}
              solid={false}
            />
            <CustomButton
              text="Save"
              handleButtonPress={requestSave}
              isButtonDisabled={(!dirtyGrid && !isCellEditing) || saving}
              isLoading={saving}
              isLoadingText="Saving..."
              solid={true}
            />
          </>
        ) : (
          <CustomButton
            text="Edit"
            handleButtonPress={() => {
              if (!canEdit) return;
              setEditMode(true);
            }}
            isButtonDisabled={!canEdit || editMode}
            solid={false}
          />
        )}
        <CustomButton
          text="Add Row"
          handleButtonPress={handleAddRow}
          isButtonDisabled={!canEdit}
          solid={false}
        />
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

interface DealStatusHistorySectionProps {
  dealId: number;
  sectionRef?: React.RefObject<HTMLDivElement | null>;
  height?: number;
  /** When false, skip the add-on list fetch. The tab stays mounted while hidden. */
  active?: boolean;
}

const DealStatusHistorySection: FC<DealStatusHistorySectionProps> = ({
  dealId,
  sectionRef,
  height = 250,
  active = true,
}) => {
  const [accordions, setAccordions] = useState<Accordion[]>([]);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    setAccordions([]);
  }, [dealId]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void (async () => {
      const res = await apiGet<Accordion[]>(`deals/${dealId}/accordions`);
      if (cancelled) return;
      if (res.success && Array.isArray(res.data)) {
        setAccordions(
          res.data.filter(
            (accordion) =>
              positiveAccordionId(accordion) != null &&
              !isInitialDealAccordion(accordion),
          ),
        );
      } else {
        setAccordions([]);
        if (!res.success) showErrorFromResponse(res);
      }
    })();
    return () => {
      cancelled = true;
    };
    // showErrorFromResponse comes from a hook and is not referentially stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, dealId]);

  const showAddonSections = accordions.length > 0;

  return (
    <Box ref={sectionRef} sx={{ mt: 0 }}>
      <StatusHistoryGrid
        resourcePath={`deal/${dealId}/status-history`}
        height={height}
        title={showAddonSections ? "Deal" : undefined}
        active={active}
      />
      {accordions.map((accordion) => {
        const accordionId = positiveAccordionId(accordion);
        if (accordionId == null) return null;
        const name = accordion.accordion_name?.trim();
        return (
          <StatusHistoryGrid
            key={accordionId}
            resourcePath={`accordions/${accordionId}/status-history`}
            height={ADDON_GRID_HEIGHT}
            title={name || `Add-on ${accordionId}`}
            stacked
            active={active}
          />
        );
      })}
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default DealStatusHistorySection;
